<template>
	<div>
		<QuestionCard
			v-if="showUpdateEnabledPanel"
			title="Should this integration update Cassie contacts?"
			description="When enabled, Cassie will use response values from your integration to automatically update contact fields. You can map multiple response fields to different Cassie fields."
			show-yes-no-buttons
			:value="useReturnedValueMappings"
			:disabled="!userFullPermissions"
			@input="setUseReturnedValueMappings"
		>
			<div
				v-if="useReturnedValueMappings"
				class="returned-value-mappings-container"
			>
				<!-- Add Mapping Inputs -->
				<fieldset
					class="d-flex flex-row cassie-horizontal-sm mapping-inputs-row"
					role="group"
					aria-labelledby="returned-value-mappings-legend"
				>
					<legend
						id="returned-value-mappings-legend"
						class="sr-only"
					>
						Add Returned Value Mapping
					</legend>
					<Dropdown
						v-model="newCassieField"
						label="Cassie Contact Field"
						class="cassie-input-width-sm"
						:items="availableCassieFields"
						:disabled="!userFullPermissions"
						placeholder="Select field..."
						aria-describedby="cassie-field-help"
					/>
					<div
						id="cassie-field-help"
						class="sr-only"
					>
						Choose the Cassie field where the returned value should be stored
					</div>
					<TextField
						v-model="newResponseField"
						label="Response Field"
						class="cassie-input-width-sm"
						placeholder="e.g., response.id"
						:rules="{max: 255, notWhitespaceOnly: true}"
						:disabled="!userFullPermissions"
						aria-describedby="response-field-help"
					/>
					<div
						id="response-field-help"
						class="sr-only"
					>
						Enter the field name or JSON path from the integration response (e.g., response.id or response.details[0].value)
					</div>
					<PrimaryActionButton
						v-if="userFullPermissions"
						:disabled="!newCassieField || !newResponseField"
						:aria-label="`Add mapping from ${newCassieField || 'Cassie field'} to ${newResponseField || 'response field'}`"
						@click="addNewMapping"
					>
						Add
					</PrimaryActionButton>
				</fieldset>

				<v-simple-table
					v-if="useReturnedValueMappings && editableMappings.length > 0"
					dense
					light
					class="returned-value-mappings-table mt-5"
					role="table"
					aria-label="Returned Value Mappings"
				>
					<template #default>
						<thead>
							<tr role="row">
								<th
									class="text-left"
									scope="col"
									style="width: 35%;"
									role="columnheader"
									aria-sort="none"
								>
									Cassie Contact Field
								</th>
								<th
									class="text-left"
									scope="col"
									style="width: 45%;"
									role="columnheader"
									aria-sort="none"
								>
									Response Field
								</th>
								<th
									v-if="userFullPermissions"
									class="text-right"
									scope="col"
									style="width: 80px"
									role="columnheader"
								>
									<span class="sr-only">
										Actions for mapping row
									</span>
									Action
								</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="(row, index) in editableMappings"
								:key="index"
								class="mapping-row"
							>
								<td class="text-left">
									{{ row.cassieField }}
								</td>
								<td class="text-left">
									{{ row.responseField }}
								</td>
								<td
									v-if="userFullPermissions"
									class="text-right"
								>
									<v-menu
										bottom
										left
										offset-y
									>
										<template #activator="{ on, attrs }">
											<v-btn
												icon
												small
												v-bind="attrs"
												v-on="on"
												@click.stop
											>
												<v-icon small>
													mdi-dots-horizontal
												</v-icon>
											</v-btn>
										</template>
										<v-list dense>
											<v-list-item @click="removeMapping(index)">
												<v-list-item-content>
													<v-list-item-title>
														Remove
													</v-list-item-title>
												</v-list-item-content>
											</v-list-item>
										</v-list>
									</v-menu>
								</td>
							</tr>
						</tbody>
					</template>
				</v-simple-table>

				<!-- Empty state message -->
				<div
					v-if="useReturnedValueMappings && editableMappings.length === 0"
					class="empty-mappings-message mt-2"
				>
					<p class="grey--text text--darken-1">
						No returned value mappings configured. Use the fields above to add your first mapping.
					</p>
				</div>
			</div>
		</QuestionCard>
	</div>
</template>

<script>
import { VSimpleTable, VBtn } from 'vuetify/lib/components'
import TextField from '../../../../../../shared/components/text-field.vue'
import Dropdown from '../../../../../../shared/components/dropdown.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import { QuestionCard } from '../../../../components/component-library-wrapper.js'

export default {
	name: 'returned-value-mappings-table',
	components: {
		VSimpleTable,
		VBtn,
		TextField,
		Dropdown,
		PrimaryActionButton,
		QuestionCard
	},
	props: {
		returnedValueMappings: {
			type: Array,
			default: () => []
		},
		// Legacy single value support for backward compatibility
		returnedValue: {
			type: String,
			default: null
		},
		objectClientUrnColumn: {
			type: String,
			default: null
		},
		userFullPermissions: Boolean,
		cassieIdentifierFields: {
			type: Array,
			default: () => []
		},
		showUpdateEnabledPanel: {
			type: Boolean,
			default: false
		},
		updateCassieContacts: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			editableMappings: [],
			useReturnedValueMappings: false,
			newCassieField: '',
			newResponseField: ''
		}
	},
	computed: {
		availableCassieFields () {
			const allFields = this.cassieIdentifierFields || []
			const usedFields = this.editableMappings.map(m => m.cassieField)
			return allFields.filter(field => {
				// Handle both object format { value: 'x' } and plain string format
				const fieldValue = typeof field === 'object' && field !== null ? field.value : field
				return !usedFields.includes(fieldValue)
			})
		}
	},
	watch: {
		returnedValueMappings: {
			handler (newMappings) {
				this.initializeEditableMappings()
				// Check if there are existing mappings to determine checkbox state
				this.useReturnedValueMappings = (newMappings && newMappings.length > 0) || this.updateCassieContacts
			},
			immediate: true
		},
		updateCassieContacts: {
			handler (newVal) {
				this.useReturnedValueMappings = newVal || (this.editableMappings && this.editableMappings.length > 0)
			},
			immediate: true
		},
		// Handle legacy single value - convert to array format
		returnedValue: {
			handler (newVal) {
				if (newVal && this.objectClientUrnColumn && (!this.returnedValueMappings || this.returnedValueMappings.length === 0)) {
					// Migrate legacy single value to new format
					this.editableMappings = [{
						cassieField: this.objectClientUrnColumn,
						responseField: newVal
					}]
					this.useReturnedValueMappings = true
				}
			},
			immediate: true
		}
	},
	mounted () {
		this.initializeEditableMappings()
	},
	methods: {
		setUseReturnedValueMappings (value) {
			this.useReturnedValueMappings = value
			this.$emit('update:updateCassieContacts', value)
			if (!value) {
				// Clear mappings when "No" is selected - use batch emit to avoid race condition (bug #23695)
				this.$emit('update:mappingsBatch', {
					returnedValueMappings: [],
					objectClientUrnColumn: null,
					returnedValue: null
				})
			} else {
				// Initialize with empty mappings when "Yes" is selected
				this.initializeEditableMappings()
				this.$forceUpdate()
			}
		},

		initializeEditableMappings () {
			if (this.returnedValueMappings && this.returnedValueMappings.length > 0) {
				this.editableMappings = [...this.returnedValueMappings]
			} else if (this.returnedValue && this.objectClientUrnColumn) {
				// Migrate legacy single value
				this.editableMappings = [{
					cassieField: this.objectClientUrnColumn,
					responseField: this.returnedValue
				}]
			} else {
				this.editableMappings = []
			}
		},

		addNewMapping () {
			const trimmedResponseField = this.newResponseField?.trim() || ''

			// Validate: response field must not be empty/whitespace and max 255 chars
			if (!trimmedResponseField || trimmedResponseField.length > 255) return

			// Validate: require Cassie field selection
			if (!this.newCassieField) return

			const newMapping = {
				cassieField: this.newCassieField,
				responseField: trimmedResponseField
			}

			this.editableMappings.push(newMapping)
			this.emitValidMappings()

			// Clear input fields
			this.newCassieField = ''
			this.newResponseField = ''
		},

		removeMapping (index) {
			this.editableMappings.splice(index, 1)
			this.emitValidMappings()
		},

		emitValidMappings () {
			const validMappings = this.editableMappings.filter(mapping =>
				mapping.cassieField && mapping.cassieField.trim() !== '' &&
				mapping.responseField && mapping.responseField.trim() !== ''
			)

			// Emit all mapping-related values together in a single event to avoid
			// race conditions where sequential emits overwrite each other due to
			// stale prop state in intermediate components (see bug #23695)
			const legacyObjectClientUrnColumn = validMappings.length > 0 ? validMappings[0].cassieField : null
			const legacyReturnedValue = validMappings.length > 0 ? validMappings[0].responseField : null

			this.$emit('update:mappingsBatch', {
				returnedValueMappings: validMappings,
				objectClientUrnColumn: legacyObjectClientUrnColumn,
				returnedValue: legacyReturnedValue
			})
		}
	}
}
</script>

<style lang="scss" scoped>
.returned-value-mappings-container {
  display: flex;
  flex-direction: column;
}

.mapping-inputs-row {
  width: calc(65% + 8px);

  .cassie-input-width-sm {
    width: auto !important;
    flex: 1;
  }
}

.empty-mappings-message {
  text-align: center;
  padding: 24px;
  border: 1px dashed var(--global-border-color);
  border-radius: var(--radius, 8px);
  background-color: var(--global-main-background-color);
  width: calc(65% + 8px);

  p {
    margin: 0;
    font-size: 14px;
  }
}

.returned-value-mappings-table {
  border: 1px solid var(--global-border-color);
  border-radius: var(--radius, 8px);
  margin-top: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  width: calc(65% + 8px) !important;

  :deep(.v-data-table__wrapper) {
    border-radius: var(--radius, 8px);
  }
}

/* Screen reader only content */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Fieldset styling to remove default browser styles */
fieldset {
  border: none;
  margin: 0;
  padding: 0;
}

legend {
  padding: 0;
}
</style>
